import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, Inbox, Search, Sparkles, X } from 'lucide-react';
import { crmApi, type ApiRecord } from '../../lib/api';
import { LeadFocusPanel } from './LeadFocusPanel';

type Tab = 'intake' | 'new' | 'attempted' | 'follow_up';

function fullName(c: ApiRecord) {
	const n = `${String(c.first_name ?? '').trim()} ${String(c.last_name ?? '').trim()}`.trim();
	return n || String(c.email ?? `#${c.id}`);
}

export function LeadsPage() {
	const [tab, setTab] = useState<Tab>('intake');
	const [focusContactId, setFocusContactId] = useState<number | null>(null);

	const intakeQuery = useQuery({
		queryKey: ['lead-intake', 'pending'],
		queryFn: () => crmApi.leadIntake({ status: 'pending', limit: 50 })
	});
	const duplicateQuery = useQuery({
		queryKey: ['lead-intake', 'duplicate'],
		queryFn: () => crmApi.leadIntake({ status: 'duplicate', limit: 20 })
	});
	const newLeads = useQuery({ queryKey: ['leads', 'new'], queryFn: () => crmApi.contacts({ lead_status: 'new', limit: 50 }) });
	const attempted = useQuery({ queryKey: ['leads', 'attempted'], queryFn: () => crmApi.contacts({ lead_status: 'attempted', limit: 50 }) });
	const followUp = useQuery({ queryKey: ['leads', 'follow_up'], queryFn: () => crmApi.contacts({ lead_status: 'follow_up', limit: 50 }) });

	const intakes = intakeQuery.data?.intakes ?? [];
	const duplicates = duplicateQuery.data?.intakes ?? [];

	const counts = {
		intake: intakes.length + duplicates.length,
		new: (newLeads.data ?? []).length,
		attempted: (attempted.data ?? []).length,
		follow_up: (followUp.data ?? []).length
	};

	return (
		<div className="space-y-3">
			<header className="rounded-2xl border border-subtle bg-surface-2 px-4 py-3 shadow-panel">
				<p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-accent">Leads</p>
				<h1 className="mt-1 text-xl font-semibold text-primary">Research queue</h1>
				<p className="mt-0.5 text-xs text-muted">
					Intake from the AI research tool lands here for review. Click any lead for focus mode (research links, notes, stage transitions, AI enhance).
				</p>
				<div className="mt-3 flex flex-wrap gap-1.5">
					{(['intake', 'new', 'attempted', 'follow_up'] as Tab[]).map((t) => (
						<button
							key={t}
							onClick={() => setTab(t)}
							className={`rounded-xl px-3 py-1.5 text-xs transition ${
								tab === t ? 'bg-accent text-accent-contrast shadow-panel' : 'border border-subtle bg-surface-2 text-secondary hover:bg-surface-hover'
							}`}
						>
							{tabLabel(t)} <span className="ml-1 rounded-full bg-surface-2/50 px-1.5 py-0.5 text-[10px]">{counts[t]}</span>
						</button>
					))}
				</div>
			</header>

			{tab === 'intake' ? (
				<IntakeQueue intakes={intakes} duplicates={duplicates} />
			) : (
				<ContactList
					contacts={(tab === 'new' ? newLeads.data : tab === 'attempted' ? attempted.data : followUp.data) ?? []}
					onSelect={(id) => setFocusContactId(id)}
				/>
			)}

			{focusContactId !== null ? (
				<LeadFocusPanel contactId={focusContactId} onClose={() => setFocusContactId(null)} />
			) : null}
		</div>
	);
}

function tabLabel(t: Tab) {
	switch (t) {
		case 'intake':
			return 'Intake queue';
		case 'new':
			return 'New';
		case 'attempted':
			return 'Attempted';
		case 'follow_up':
			return 'Follow-up';
	}
}

function ContactList({ contacts, onSelect }: { contacts: ApiRecord[]; onSelect: (id: number) => void }) {
	if (contacts.length === 0) {
		return (
			<div className="rounded-2xl border border-dashed border-subtle px-4 py-6 text-sm text-muted">
				<Sparkles className="mr-1 inline h-4 w-4 text-muted" />
				Nothing in this list yet.
			</div>
		);
	}
	return (
		<ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
			{contacts.map((c) => (
				<li key={String(c.id)}>
					<button
						onClick={() => onSelect(Number(c.id))}
						className="w-full rounded-xl border border-subtle bg-canvas/60 px-3 py-2.5 text-left transition hover:border-white/30 hover:bg-canvas"
					>
						<p className="truncate text-sm font-medium text-primary">{fullName(c)}</p>
						<p className="truncate text-xs text-muted">
							{String((c.company as ApiRecord | null)?.name ?? '')}
							{c.title ? ` · ${String(c.title)}` : ''}
						</p>
						<p className="mt-1 truncate text-[11px] text-muted">{String(c.email ?? '')}</p>
					</button>
				</li>
			))}
		</ul>
	);
}

function IntakeQueue({ intakes, duplicates }: { intakes: ApiRecord[]; duplicates: ApiRecord[] }) {
	if (intakes.length === 0 && duplicates.length === 0) {
		return (
			<div className="rounded-2xl border border-dashed border-subtle px-4 py-6 text-sm text-muted">
				<Inbox className="mr-1 inline h-4 w-4 text-muted" />
				The intake queue is empty. Your AI research tool can POST leads to <code className="rounded bg-surface-2 px-1.5 py-0.5 text-[11px] text-accent">/api/leads/intake</code>.
			</div>
		);
	}
	return (
		<div className="space-y-3">
			{intakes.length > 0 ? (
				<section className="rounded-2xl border border-subtle bg-surface-2 p-3 shadow-panel">
					<h2 className="text-sm font-semibold text-primary">Pending review ({intakes.length})</h2>
					<ul className="mt-2 space-y-2">
						{intakes.map((i) => (
							<IntakeRow key={String(i.id)} intake={i} />
						))}
					</ul>
				</section>
			) : null}
			{duplicates.length > 0 ? (
				<section className="rounded-2xl border border-amber-400/30 bg-amber-500/5 p-3 shadow-panel">
					<h2 className="text-sm font-semibold text-amber-200">Likely duplicates ({duplicates.length})</h2>
					<p className="text-[11px] text-amber-200/70">Email matched an existing contact. Merge or reject.</p>
					<ul className="mt-2 space-y-2">
						{duplicates.map((i) => (
							<IntakeRow key={String(i.id)} intake={i} isDuplicate />
						))}
					</ul>
				</section>
			) : null}
		</div>
	);
}

function IntakeRow({ intake, isDuplicate }: { intake: ApiRecord; isDuplicate?: boolean }) {
	const queryClient = useQueryClient();
	const [editing, setEditing] = useState(false);
	const [form, setForm] = useState({
		company_name: String(intake.company_name ?? ''),
		website_domain: String(intake.website_domain ?? ''),
		first_name: String(intake.first_name ?? ''),
		last_name: String(intake.last_name ?? ''),
		email: String(intake.email ?? ''),
		phone: String(intake.phone ?? ''),
		title: String(intake.title ?? '')
	});
	const refresh = () => queryClient.invalidateQueries({ queryKey: ['lead-intake'] });

	const approve = useMutation({
		mutationFn: () =>
			crmApi.leadIntakeApprove(Number(intake.id), {
				company_name: form.company_name || null,
				website_domain: form.website_domain || null,
				first_name: form.first_name,
				last_name: form.last_name,
				email: form.email,
				phone: form.phone || null,
				title: form.title || null
			}),
		onSuccess: () => {
			toast.success('Lead approved');
			refresh();
		},
		onError: (e) => toast.error(e instanceof Error ? e.message : 'Approve failed')
	});
	const reject = useMutation({
		mutationFn: () => crmApi.leadIntakeReject(Number(intake.id)),
		onSuccess: () => {
			toast.success('Lead rejected');
			refresh();
		}
	});
	const merge = useMutation({
		mutationFn: () => crmApi.leadIntakeMerge(Number(intake.id), Number(intake.matched_contact_id)),
		onSuccess: () => {
			toast.success('Merged into existing contact');
			refresh();
		}
	});

	const displayName = `${form.first_name} ${form.last_name}`.trim() || form.email || `Intake #${intake.id}`;

	return (
		<li className="rounded-xl border border-subtle bg-canvas/60 px-3 py-2.5">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-medium text-primary">{displayName}</p>
					<p className="truncate text-xs text-muted">
						{form.company_name || <span className="italic text-muted">no company</span>}
						{form.title ? ` · ${form.title}` : ''}
					</p>
					<p className="mt-0.5 text-[11px] text-muted">
						source: <span className="text-secondary">{String(intake.source)}</span>
						{intake.external_ref ? ` · ref ${String(intake.external_ref)}` : ''}
					</p>
				</div>
				<div className="flex shrink-0 gap-1.5">
					{isDuplicate && intake.matched_contact_id ? (
						<button onClick={() => merge.mutate()} className="rounded-lg border border-amber-400/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-200 hover:bg-amber-500/20">
							Merge into #{String(intake.matched_contact_id)}
						</button>
					) : null}
					<button onClick={() => setEditing((v) => !v)} className="rounded-lg border border-subtle bg-surface-2 px-2 py-1 text-[11px] text-secondary hover:bg-surface-hover">
						{editing ? 'Hide' : 'Edit'}
					</button>
					<button onClick={() => approve.mutate()} disabled={approve.isPending} className="rounded-lg bg-emerald-500/90 px-2 py-1 text-[11px] font-medium text-accent-contrast hover:bg-emerald-400">
						<Check className="inline h-3 w-3" /> Approve
					</button>
					<button onClick={() => reject.mutate()} disabled={reject.isPending} className="rounded-lg border border-rose-400/30 bg-danger/10 px-2 py-1 text-[11px] text-danger hover:bg-danger/15">
						<X className="inline h-3 w-3" /> Reject
					</button>
				</div>
			</div>
			{editing ? (
				<div className="mt-2 grid gap-1.5 sm:grid-cols-2">
					{(['first_name', 'last_name', 'email', 'phone', 'company_name', 'website_domain', 'title'] as const).map((k) => (
						<input
							key={k}
							placeholder={k.replace('_', ' ')}
							value={form[k]}
							onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
							className="rounded-lg border border-subtle bg-canvas px-2 py-1 text-xs text-primary"
						/>
					))}
				</div>
			) : null}
			{intake.notes ? <p className="mt-2 text-[11px] text-muted">{String(intake.notes)}</p> : null}
			{intake.raw_payload ? (
				<details className="mt-1.5">
					<summary className="cursor-pointer text-[10px] text-muted hover:text-secondary">
						<Search className="mr-1 inline h-3 w-3" /> raw payload
					</summary>
					<pre className="mt-1 max-h-40 overflow-auto rounded bg-surface-2/80 p-2 text-[10px] text-secondary">{String(intake.raw_payload)}</pre>
				</details>
			) : null}
		</li>
	);
}
